import React, { useEffect } from 'react';

const PrototypePattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Prototype Pattern - Creational Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Prototype Pattern - create objects by cloning existing instances for efficient object creation. Learn with document templates and real-world examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Prototype Pattern - create objects by cloning existing instances for efficient object creation. Learn with document templates and real-world examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'prototype pattern, design patterns, creational patterns, object cloning, performance optimization, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'prototype pattern, design patterns, creational patterns, object cloning, performance optimization, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Prototype Pattern</h1>
        <p style={styles.subtitle}>
          Creational Design Pattern - Create objects by cloning existing instances
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Prototype pattern allows you to create new objects by cloning existing instances. 
            It's useful when object creation is expensive or when you need to create objects 
            that are similar to existing ones.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            Sometimes creating an object from scratch is expensive (database queries, network calls, 
            complex calculations). The Prototype pattern allows you to clone existing objects instead 
            of creating new ones, which can be much more efficient.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Document Templates</h2>
          <div style={styles.codeBlock}>
{`// Prototype interface
interface Cloneable {
    Object clone();
}

// Abstract prototype
abstract class Document implements Cloneable {
    protected String title;
    protected String content;
    protected List<String> images;
    protected Map<String, String> metadata;
    
    public Document() {
        this.images = new ArrayList<>();
        this.metadata = new HashMap<>();
    }
    
    // Copy constructor for cloning
    public Document(Document document) {
        this.title = document.title;
        this.content = document.content;
        this.images = new ArrayList<>(document.images);
        this.metadata = new HashMap<>(document.metadata);
    }
    
    public abstract Document clone();
    
    // Getters and setters
    public void setTitle(String title) { this.title = title; }
    public void setContent(String content) { this.content = content; }
    public void addImage(String image) { this.images.add(image); }
    public void addMetadata(String key, String value) { this.metadata.put(key, value); }
}

// Concrete prototypes
class ReportDocument extends Document {
    private String reportType;
    private Date generatedDate;
    
    public ReportDocument() {
        super();
        this.reportType = "General";
        this.generatedDate = new Date();
    }
    
    public ReportDocument(ReportDocument document) {
        super(document);
        this.reportType = document.reportType;
        this.generatedDate = new Date(document.generatedDate.getTime());
    }
    
    @Override
    public ReportDocument clone() {
        return new ReportDocument(this);
    }
    
    public void setReportType(String reportType) { this.reportType = reportType; }
    public String getReportType() { return reportType; }
}

// Prototype registry
class DocumentRegistry {
    private Map<String, Document> prototypes = new HashMap<>();
    
    public void registerPrototype(String key, Document prototype) {
        prototypes.put(key, prototype);
    }
    
    public Document createDocument(String key) {
        Document prototype = prototypes.get(key);
        if (prototype != null) {
            return prototype.clone();
        }
        throw new IllegalArgumentException("No prototype found for key: " + key);
    }
}

// Usage
public class PrototypeDemo {
    public static void main(String[] args) {
        // Create prototype registry
        DocumentRegistry registry = new DocumentRegistry();
        
        // Create and configure prototypes
        ReportDocument reportPrototype = new ReportDocument();
        reportPrototype.setTitle("Monthly Report Template");
        reportPrototype.setContent("This is a template for monthly reports...");
        reportPrototype.setReportType("Monthly");
        reportPrototype.addImage("logo.png");
        reportPrototype.addMetadata("department", "Sales");
        
        // Register prototype
        registry.registerPrototype("monthly_report", reportPrototype);
        
        // Clone documents from prototypes
        ReportDocument report1 = (ReportDocument) registry.createDocument("monthly_report");
        report1.setTitle("January Sales Report");
        report1.addMetadata("month", "January");
        
        ReportDocument report2 = (ReportDocument) registry.createDocument("monthly_report");
        report2.setTitle("February Sales Report");
        report2.addMetadata("month", "February");
        
        // Verify they are different instances
        System.out.println("report1 == report2: " + (report1 == report2)); // false
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Performance:</strong> Avoids expensive object creation</li>
            <li style={styles.listItem}><strong>Flexibility:</strong> Can create objects at runtime</li>
            <li style={styles.listItem}><strong>Reduced Subclassing:</strong> Alternative to creating many subclasses</li>
            <li style={styles.listItem}><strong>Dynamic Configuration:</strong> Can configure prototypes at runtime</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When object creation is expensive</li>
            <li style={styles.listItem}>When you need objects similar to existing ones</li>
            <li style={styles.listItem}>When you want to avoid subclassing</li>
            <li style={styles.listItem}>When you need to create objects dynamically</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Prototype pattern is valuable when you need to create new objects efficiently by 
            copying existing ones. It's particularly useful for complex objects that are expensive 
            to create from scratch.
          </p>
        </section>
      </main>
    </article>
  );
};

export default PrototypePattern;